import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { JOB_PORTAL_KEYS, NS } from '../../../../i18n/constants/keys';
import { useJobPostingFilter } from '../hooks';
import type { IJobPostingFilterProps } from '../interfaces';

export const JobPostingFilter = ({ filters, setFilters, setSearch, search }: IJobPostingFilterProps) => {
  const { t } = useTranslation(NS.jobPortal);
  const filter = useJobPostingFilter({ filters, setFilters, setSearch, search });

  return (
    <Box sx={{ bgcolor: 'background.paper', p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
          Refine roles
        </Typography>
        {search && (
          <Button variant="text" size="small" onClick={filter.handleClearFilters}>
            {t(JOB_PORTAL_KEYS.filter.clearFilter)}
          </Button>
        )}
      </Stack>
      <Stack spacing={1.5}>
        <Autocomplete
          options={filter.jobTitles}
          value={filters.jobTitle[0] ?? null}
          onChange={(_e, value) => filter.handleTitleLocationChange('jobTitle', value)}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder={t(JOB_PORTAL_KEYS.filter.searchByJobTitle)} />
          )}
          fullWidth
        />
        <Autocomplete
          options={filter.locations}
          value={filters.location[0] ?? null}
          onChange={(_e, value) => filter.handleTitleLocationChange('location', value)}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder={t(JOB_PORTAL_KEYS.filter.searchByLocation)} />
          )}
          fullWidth
        />
        <Autocomplete
          multiple
          options={filter.workTypeOptions.map((option) => option.value)}
          getOptionLabel={(value) => filter.workTypeOptions.find((option) => option.value === value)?.label ?? value}
          value={filters.workType}
          onChange={(_e, values) => filter.handleWorkTypeChange(values)}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder={t(JOB_PORTAL_KEYS.filter.selectWorkType)} />
          )}
          fullWidth
        />
        <Autocomplete
          multiple
          options={filter.departments}
          value={filters.department}
          onChange={(_e, values) => filter.handleDepartmentChange(values)}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder={t(JOB_PORTAL_KEYS.filter.selectDepartment)} />
          )}
          fullWidth
        />
        <Button variant="contained" fullWidth onClick={filter.handleSearch} sx={{ mt: 0.5 }}>
          {t(JOB_PORTAL_KEYS.filter.search)}
        </Button>
      </Stack>
    </Box>
  );
};
